"use client";

import { AlgoliaInsightsMiddleware, getAlgoliaClient } from "@entities/algolia";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useClientConfig } from "@providers/app-provider/client-app-provider";
import { Button, cn, Input, Popover, PopoverContent, PopoverTrigger, useIsTablet } from "@ubud-ui/react-ui-kit";
import { useSearchParams } from "next/navigation";
import { useLayoutEffect, useRef, useState } from "react";
import { Configure, InstantSearch } from "react-instantsearch";
import { AlgoliaCompanyResult } from "./algolia-company-result";
import { AlgoliaGovernmentResult } from "./algolia-government-result";
import { AlgoliaIndustrialVacancyResult } from "./algolia-industrial-vacancy-result";
import { CityResult } from "./city-result";
import { CompanyResult } from "./company-result";
import { GovernmentResult } from "./government-result";
import { HeaderSearchBox } from "./header-search-box";
import { IndustrialVacancyResult } from "./industrial-vacancy-result";
import { JobFunctionResult } from "./job-function-result";
import { MicroVacancyResult } from "./micro-vacancy-result";
import { OtherResult } from "./other-result";
import { SkillResult } from "./skill-result";

export function HeaderSearchBar({ onSearchOpen }: { onSearchOpen: (open: boolean) => void }) {
  const params = useSearchParams();

  const isTablet = useIsTablet();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState(params.get("keyword") || "");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = useRef<HTMLButtonElement>(null);
  const [searchInputWidth, setSearchInputWidth] = useState<number | undefined>();
  const [isSearching, setIsSearching] = useState<boolean>(false);

  useLayoutEffect(() => {
    if (searchInputRef.current) {
      const updateWidth = () => setSearchInputWidth(searchInputRef.current!.offsetWidth);
      updateWidth();

      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }
  }, [isSearchOpen]);

  const handleToggleSearchBar = (open: boolean) => {
    if (onSearchOpen) {
      onSearchOpen(open);
    }
    setIsSearchOpen(open);
  };

  const clientConfig = useClientConfig();
  const config = clientConfig?.config;
  const algoliaConfig = config?.algolia;

  const searchClient = getAlgoliaClient(algoliaConfig);

  return (
    <>
      <div className="flex items-center gap-4 flex-1">
        <Popover open={isSearchFocused} onOpenChange={setIsSearchFocused}>
          <PopoverTrigger asChild>
            <Button
              ref={searchInputRef}
              variant="outline"
              role="combobox"
              aria-expanded={isSearchFocused}
              className={cn(
                "text-sm transition-all transform w-full text-left justify-between pl-0",
                !isSearchFocused ? "rounded-lg" : "",
                isTablet ? (!isSearchOpen ? "opacity-0 translate-x-[100%] invisible" : "opacity-100 translate-x-0") : "",
              )}
            >
              <Input defaultValue={keyword} placeholder="Cari pekerjaan yang kamu inginkan" className="m-0 pl-4 border-0 text-sm" />
              <label className="h-full flex items-center gap-3 sticky right-0">
                <hr className="h-full w-[1px] bg-gray-200" />
                <MagnifyingGlassIcon className={cn("size-5 min-w-5", !!keyword ? "text-gray-900" : "text-gray-500")} />
              </label>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="p-0 -mt-11 overflow-y-auto max-h-[calc(80vh_-_36px)]"
            align="start"
            style={{ width: searchInputWidth ? `${searchInputWidth}px` : "100%" }}
          >
            <div className="p-1">
              {searchClient && (
                <InstantSearch searchClient={searchClient}>
                  <Configure hitsPerPage={3} />
                  <HeaderSearchBox
                    keyword={keyword}
                    initialKeyword={params.get("keyword")}
                    onSearching={(isSearching) => setIsSearching(isSearching || false)}
                    onSearch={setKeyword}
                  />
                  {isSearching && (
                    <>
                      <div className="text-center flex items-center p-4 w-full">Sedang mencari...</div>
                    </>
                  )}
                  {keyword ? (
                    <>
                      <AlgoliaIndustrialVacancyResult onClick={() => setIsSearchFocused(false)} />
                      <AlgoliaCompanyResult onClick={() => setIsSearchFocused(false)} />
                      <AlgoliaGovernmentResult onClick={() => setIsSearchFocused(false)} />
                      <AlgoliaInsightsMiddleware />
                    </>
                  ) : (
                    <>
                      <IndustrialVacancyResult keyword={keyword} onClick={() => setIsSearchFocused(false)} />
                      <CompanyResult keyword={keyword} onClick={() => setIsSearchFocused(false)} />
                      <GovernmentResult keyword={keyword} onClick={() => setIsSearchFocused(false)} />
                    </>
                  )}
                </InstantSearch>
              )}
              {/* <HeaderSearchBox keyword={keyword} initialKeyword={params.get("keyword")} onSearch={setKeyword} />
              <IndustrialVacancyResult keyword={keyword} onClick={() => setIsSearchFocused(false)} />
              <CompanyResult keyword={keyword} onClick={() => setIsSearchFocused(false)} /> */}
              {!isSearching && (
                <>
                  {keyword && (
                    <>
                      <MicroVacancyResult keyword={keyword} onClick={() => setIsSearchFocused(false)} />
                      <OtherResult keyword={keyword} onClick={() => setIsSearchFocused(false)} />
                      <CityResult keyword={keyword} onClick={() => setIsSearchFocused(false)} />
                      <SkillResult keyword={keyword} onClick={() => setIsSearchFocused(false)} />
                    </>
                  )}
                  <JobFunctionResult keyword={keyword} onClick={() => setIsSearchFocused(false)} />
                </>
              )}
            </div>
          </PopoverContent>
        </Popover>
        {!isSearchOpen ? (
          <MagnifyingGlassIcon className="min-w-6 size-6 lg:hidden cursor-pointer" onClick={() => handleToggleSearchBar(true)} />
        ) : (
          <XMarkIcon className="min-w-6 size-6 lg:hidden cursor-pointer" onClick={() => handleToggleSearchBar(false)} />
        )}
      </div>
    </>
  );
}
